import { useEffect } from 'react';
import { Box, Image } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';

import successState from 'Asset/image/siglive-success-state.svg';
import warnState from 'Asset/image/siglive-warn-state.svg';
import { Condition, Iteration } from 'Component/others';
import HeaderMobile from 'Component/header-mobile';
import { useStatusState } from 'Store/status-state';
import { TextBold, TextExtraBold, TextMedium, TextRegular } from 'UiKit/typography';
import ButtonSolid from 'UiKit/button/buttonSolid';
import ButtonLink from 'UiKit/button/buttonLink';

import violationTypeToText from 'Helper/violationTypeToText';

import styles from './styles.module.css';

const imageMap = {
  success: successState,
  warning: warnState,
};

const StatusState = () => {
  const { t } = useTranslation('host-attendance');
  const { isOpen, type, title, description, violations, onClick, primaryButtonLabel, lateClockInMinutes, onResetStatusState } = useStatusState();

  const handlePrimaryClick = () => {
    onClick?.();
    onResetStatusState();
  };

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const hasViolations = !isEmpty(violations);

  const transformedViolations = violations?.map((violation) => violationTypeToText(violation, t));

  return (
    <Box className={styles['status-state__container']}>
      <HeaderMobile type="header-close" onClick={handlePrimaryClick} />
      <Image src={imageMap[type]} alt={type} className={styles['status-state__image']} />
      <Box className={styles['status-state__text']}>
        <TextExtraBold className={styles['status-state__title']}>{title}</TextExtraBold>
        <TextMedium className={styles['status-state__description']} dangerouslySetInnerHTML={{ __html: description }} />
        <Condition statement={type === 'warning'}>
          <Condition statement={hasViolations}>
            <Box className={styles['violation__container']}>
              <Iteration
                items={transformedViolations}
                render={(violation, index) => (
                  <TextMedium key={index} fontSize="var(--font-body1)">
                    {'• '}
                    {violation.label}
                    {violation.key === 'LATE_CLOCK_IN' && lateClockInMinutes > 0 && (
                      <>
                        {' : '}
                        <TextBold as="span" fontSize="var(--font-body1)">
                          {lateClockInMinutes} {t('minutes')}
                        </TextBold>
                      </>
                    )}
                  </TextMedium>
                )}
              />
            </Box>
          </Condition>
          <TextRegular className={styles['status-state__warning']} marginTop={isEmpty(violations) && '16px'}>
            {t('violation_detected.contact')}
          </TextRegular>
        </Condition>
      </Box>
      <Box className={styles['status-state__button-container']}>
        <ButtonSolid className={styles['status-state__button']} onClick={handlePrimaryClick}>
          {primaryButtonLabel ?? t('common:done')}
        </ButtonSolid>
        <Condition statement={!!primaryButtonLabel}>
          <ButtonLink onClick={onResetStatusState} width="100%" marginTop="8px">
            {t('common:done')}
          </ButtonLink>
        </Condition>
      </Box>
    </Box>
  );
};

export default StatusState;
